import TeamMemberFilter from './TeamMemberFilter';
import CertificationStatusFilter from './CertificationStatusFilter';
import TeamFilter from './TeamFilter';
import PlaylistFilter from './PlaylistFilter';
import ContentFilter from './ContentFilter';
import ContentTypeFilter from './ContentTypeFilter';
import DueDateFilter from './DueDateFilter';
import CompletedDateFilter from './CompletedDateFilter.tsx';
import TopicSelect from '../common/TopicSelect';
import { Box } from 'foundations-components/transitional/components/Box';
import DebouncedSearchInputFilter from './DebouncedSearchInputFilter';
import { FilterButton } from './FilterButton';
import { useIsAnyFilterSet } from '../../hooks/filtersHooks';
import { commonFilters } from '../../constants/filters';
import MyTeamFiltersWrapper from './MyTeamFiltersWrapper';
import UsageTracker from '../../../../utils/UsageTracker';
import { trackCertificationStatusChange } from '../../utils/trackingUtils';
import { useCallback } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const CertificationStatusesFilterButton = ({
  certificationStatuses,
  setCertificationStatuses
}) => {
  const handleStatusChange = statuses => {
    setCertificationStatuses(statuses);
    if (statuses.length > certificationStatuses.length) {
      const newStatus = statuses.find(status => !certificationStatuses.includes(status));
      if (newStatus) {
        trackCertificationStatusChange(newStatus);
      }
    }
  };
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "certification-status",
    isFilterActive: certificationStatuses.length > 0,
    onClear: () => setCertificationStatuses([]),
    children: /*#__PURE__*/_jsx(CertificationStatusFilter, {
      onChange: handleStatusChange,
      selectedCertificationStatuses: certificationStatuses
    })
  });
};
export const TopicSelectFilterButton = ({
  categories,
  setCategories,
  tools,
  setTools
}) => {
  const handleTopicChange = selectedTopics => {
    UsageTracker.track('teamInteraction', {
      action: 'filter-selected',
      type: 'catalog-topic'
    });
    setCategories(selectedTopics.categories);
    setTools(selectedTopics.tools);
  };
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "catalog-topic",
    isFilterActive: categories.length > 0 || tools.length > 0,
    onClear: () => {
      setCategories([]);
      setTools([]);
    },
    children: /*#__PURE__*/_jsx(TopicSelect, {
      onChange: handleTopicChange,
      selectedTopics: {
        categories,
        tools
      }
    })
  });
};
export const TeamsFilterButton = ({
  teams,
  setTeams
}) => {
  const handleTeamsChange = selectedTeams => {
    if (selectedTeams.length > teams.length) {
      UsageTracker.track('teamInteraction', {
        action: 'filter-selected',
        type: 'teams'
      });
    }
    setTeams(selectedTeams);
  };
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "teams",
    isFilterActive: teams.length > 0,
    onClear: () => setTeams([]),
    children: /*#__PURE__*/_jsx(TeamFilter, {
      onChange: handleTeamsChange,
      selectedTeams: teams
    })
  });
};
export const TeamMemberFilterButton = ({
  users,
  setUsers
}) => {
  const handleUsersChange = selectedUsers => {
    if (selectedUsers.length > users.length) {
      UsageTracker.track('teamInteraction', {
        action: 'filter-selected',
        type: 'team-members'
      });
    }
    setUsers(selectedUsers);
  };
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "team-members",
    isFilterActive: users.length > 0,
    onClear: () => setUsers([]),
    children: /*#__PURE__*/_jsx(TeamMemberFilter, {
      onChange: handleUsersChange,
      selectedUsers: users
    })
  });
};
export const PlaylistsFilterButton = ({
  playlists,
  setPlaylists
}) => {
  const handlePlaylistsChange = selectedPlaylists => {
    if (selectedPlaylists.length > playlists.length) {
      UsageTracker.track('teamInteraction', {
        action: 'filter-selected',
        type: 'playlists'
      });
    }
    setPlaylists(selectedPlaylists);
  };
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "playlists",
    isFilterActive: playlists.length > 0,
    onClear: () => setPlaylists([]),
    children: /*#__PURE__*/_jsx(PlaylistFilter, {
      onChange: handlePlaylistsChange,
      selectedPlaylists: playlists
    })
  });
};
export const ContentFilterButton = ({
  contentIds,
  setContentIds
}) => {
  const handleContentChange = selectedContentIds => {
    if (selectedContentIds.length > contentIds.length) {
      UsageTracker.track('teamInteraction', {
        action: 'filter-selected',
        type: 'content'
      });
    }
    setContentIds(selectedContentIds);
  };
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "content",
    isFilterActive: contentIds.length > 0,
    onClear: () => setContentIds([]),
    children: /*#__PURE__*/_jsx(ContentFilter, {
      onChange: handleContentChange,
      selectedContentIds: contentIds
    })
  });
};
export const ContentTypeFilterButton = ({
  contentTypes,
  setContentTypes
}) => {
  const handleContentTypeChange = selectedTypes => {
    if (selectedTypes.length > contentTypes.length) {
      UsageTracker.track('teamInteraction', {
        action: 'filter-selected',
        type: 'content-type'
      });
    }
    setContentTypes(selectedTypes);
  };
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "content-type",
    isFilterActive: contentTypes.length > 0,
    onClear: () => setContentTypes([]),
    children: /*#__PURE__*/_jsx(ContentTypeFilter, {
      onChange: handleContentTypeChange,
      selectedTypes: contentTypes
    })
  });
};
export const DueDateFilterButton = ({
  dueDate,
  setDueDate
}) => {
  const handleDueDateChange = useCallback(newDueDate => {
    if (newDueDate) {
      UsageTracker.track('teamInteraction', {
        action: 'filter-selected',
        type: 'due-date',
        value: newDueDate
      });
    }
    setDueDate(newDueDate);
  }, [setDueDate]);
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "due-date",
    isFilterActive: !!dueDate,
    onClear: () => setDueDate(undefined),
    children: /*#__PURE__*/_jsx(DueDateFilter, {
      onChange: handleDueDateChange,
      value: dueDate
    })
  });
};
export const CompletedDateFilterButton = ({
  completedDate,
  setCompletedDate
}) => {
  const handleCompletedDateChange = useCallback(newCompletedDate => {
    if (newCompletedDate) {
      UsageTracker.track('teamInteraction', {
        action: 'filter-selected',
        type: 'completed-date',
        value: newCompletedDate
      });
    }
    setCompletedDate(newCompletedDate);
  }, [setCompletedDate]);
  return /*#__PURE__*/_jsx(FilterButton, {
    filterType: "completed-date",
    isFilterActive: !!completedDate,
    onClear: () => setCompletedDate(undefined),
    children: /*#__PURE__*/_jsx(CompletedDateFilter, {
      onChange: handleCompletedDateChange,
      value: completedDate
    })
  });
};
const Filters = ({
  store
}) => {
  const {
    state: {
      certificationStatuses,
      categories,
      tools,
      teams,
      users,
      search
    },
    actions: {
      setCertificationStatuses,
      setCategories,
      setSearch,
      setTools,
      setTeams,
      setUsers,
      resetTableState
    }
  } = store;
  const isAnyFilterSet = useIsAnyFilterSet(store, commonFilters.filter(filter => filter !== 'search') // search has its own clear button
  );
  const handleSearchChange = useCallback(newSearch => {
    setSearch(newSearch);
    if (newSearch) {
      UsageTracker.track('teamInteraction', {
        action: 'search-input',
        value: newSearch
      });
    }
  }, [setSearch]);
  return /*#__PURE__*/_jsxs(MyTeamFiltersWrapper, {
    isAnyFilterSet: isAnyFilterSet,
    onClearAllFilters: resetTableState,
    searchInput: /*#__PURE__*/_jsx(DebouncedSearchInputFilter, {
      onChange: handleSearchChange,
      value: search
    }),
    tableStore: store,
    children: [/*#__PURE__*/_jsx(Box, {
      children: /*#__PURE__*/_jsx(CertificationStatusesFilterButton, {
        certificationStatuses: certificationStatuses,
        setCertificationStatuses: setCertificationStatuses
      })
    }), /*#__PURE__*/_jsx(Box, {
      children: /*#__PURE__*/_jsx(TopicSelectFilterButton, {
        categories: categories,
        setCategories: setCategories,
        setTools: setTools,
        tools: tools
      })
    }), /*#__PURE__*/_jsx(Box, {
      children: /*#__PURE__*/_jsx(TeamsFilterButton, {
        setTeams: setTeams,
        teams: teams
      })
    }), /*#__PURE__*/_jsx(Box, {
      children: /*#__PURE__*/_jsx(TeamMemberFilterButton, {
        setUsers: setUsers,
        users: users
      })
    })]
  });
};
export default Filters;